import { FormEvent, useEffect, useState } from 'react'

import Button from '@material-ui/core/Button'
import Dialog from '@material-ui/core/Dialog'
import DialogContent from '@material-ui/core/DialogContent'
import DialogTitle from '@material-ui/core/DialogTitle'
import IconButton from '@material-ui/core/IconButton'
import { withStyles, WithStyles } from '@material-ui/core/styles'
import TextField from '@material-ui/core/TextField'
import Typography from '@material-ui/core/Typography'
import CloseIcon from '@material-ui/icons/Close'
import { isNull } from 'lodash'
import { connect } from 'react-redux'
import { Dispatch } from 'redux'
import { RootState } from 'store/store'

import locationPickerTreeActions from '../../../../../../components/_shared/customTree/services/locationPickerTree-actions'
import { checkConnection } from '../../../../../../services/root/root-service'
import {
  getLocationFilterParams,
  LocationFilter,
  PickerOrganization,
} from '../../../../services/locationPicker-service'
import deviceDetailsActions from '../../services/deviceDetails-actions'
import LocationChip from '../addDeviceButton/locationPicker/LocationChip'
import OrganizationSelect from '../addDeviceButton/locationPicker/OrganizationSelect'
import styles from '../addDeviceButton/style'

type OwnProps = {
  open: boolean
  onClose: () => void
  deviceId: string
  organizationId?: number | string
  organizationName?: string
  allowedLocationIds?: number[] | null
  nickname?: string
}

type EditArcAssignmentModalProps = OwnProps &
  ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps> &
  WithStyles<typeof styles>

const EditArcAssignmentModal = ({
  classes,
  open,
  onClose,
  deviceId,
  organizationId,
  organizationName,
  allowedLocationIds,
  nickname: initialNickname = '',
  pickerOrganizations,
  isLoading,
  error,
  assignArcDevice,
  loadLocationTreeForOrg,
  resetTreeState,
  resetError,
  treeCopy,
}: EditArcAssignmentModalProps) => {
  const [nickname, setNickname] = useState(initialNickname)
  const [selectedOrganization, setSelectedOrganization] =
    useState<PickerOrganization | null>(null)
  const [locationFilter, setLocationFilter] = useState<LocationFilter>({
    selectedName: '',
    selectedBuildings: null,
  })

  useEffect(() => {
    if (!open) {
      return
    }
    resetError()
    setNickname(initialNickname)
    const orgId = organizationId ? Number(organizationId) : null
    if (orgId) {
      const organization =
        pickerOrganizations.find((org) => org.id === orgId) ||
        (organizationName ? { id: orgId, name: organizationName } : null)
      setSelectedOrganization(organization)
      const locationIds = allowedLocationIds ?? null
      loadLocationTreeForOrg(orgId, locationIds)
    } else {
      setSelectedOrganization(null)
      setLocationFilter({ selectedName: '', selectedBuildings: null })
      resetTreeState()
    }
  }, [
    open,
    organizationId,
    allowedLocationIds,
    initialNickname,
    organizationName,
    pickerOrganizations,
    loadLocationTreeForOrg,
    resetError,
    resetTreeState,
  ])

  useEffect(() => {
    if (open && treeCopy?.children) {
      setLocationFilter(
        getLocationFilterParams(treeCopy, allowedLocationIds ?? null)
      )
    }
  }, [open, treeCopy, allowedLocationIds])

  const handleOrganizationChange = (
    organization: PickerOrganization | null
  ) => {
    setSelectedOrganization(organization)
    if (organization) {
      loadLocationTreeForOrg(organization.id, null)
      setLocationFilter({ selectedName: '', selectedBuildings: null })
    } else {
      resetTreeState()
      setLocationFilter({ selectedName: '', selectedBuildings: null })
    }
  }

  const handleClose = () => {
    resetTreeState()
    onClose()
  }

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault()
    const organizationIdValue = selectedOrganization?.id ?? null
    const locationIds = locationFilter.selectedBuildings
    assignArcDevice(
      deviceId,
      organizationIdValue,
      isNull(locationIds) ? null : locationIds,
      nickname || null,
      handleClose
    )
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="edit-device-title"
    >
      <DialogTitle className={classes.dialogTitle} disableTypography>
        <IconButton
          className={classes.statusFilerIcon}
          aria-label="close"
          onClick={handleClose}
        >
          <CloseIcon />
        </IconButton>
        <Typography variant="h4">Edit Device</Typography>
        {error && (
          <Typography
            variant="subtitle1"
            color="error"
            className={classes.error}
          >
            {error}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <form onSubmit={handleSubmit} noValidate>
          <TextField
            variant="outlined"
            label="Nickname"
            value={nickname}
            onChange={(event) => setNickname(event.target.value)}
            fullWidth
            margin="normal"
          />
          <OrganizationSelect
            value={selectedOrganization}
            onChange={handleOrganizationChange}
          />
          {selectedOrganization && (
            <LocationChip
              locationFilter={locationFilter}
              onLocationFilterChange={setLocationFilter}
            />
          )}
          <Button
            variant="contained"
            color="primary"
            type="submit"
            className={classes.actionButton}
            disabled={isLoading}
          >
            Save Changes
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  )
}

const mapStateToProps = (state: RootState) => ({
  pickerOrganizations:
    state.devicesReducer.deviceDetailsReducer.pickerOrganizations,
  isLoading: state.devicesReducer.deviceDetailsReducer.isLoading,
  error: state.devicesReducer.deviceDetailsReducer.error || '',
  treeCopy: state.locationPickerTreeReducer.treeCopy,
})

const mapDispatchToProps = (dispatch: Dispatch) => ({
  assignArcDevice: (
    deviceId: string,
    organizationId: number | null,
    locationIds: number[] | null,
    nickname: string | null,
    onSuccess: () => void
  ) =>
    dispatch(
      checkConnection(() =>
        deviceDetailsActions.assignArcDevice(
          deviceId,
          organizationId,
          locationIds,
          nickname,
          onSuccess
        )
      )
    ),
  loadLocationTreeForOrg: (
    organizationId: number,
    allowedLocationIds: number[] | null
  ) =>
    dispatch(
      checkConnection(() =>
        deviceDetailsActions.loadLocationTreeForOrg(
          organizationId,
          allowedLocationIds
        )
      )
    ),
  resetTreeState: () => dispatch(locationPickerTreeActions.resetTreeState()),
  resetError: () => dispatch(deviceDetailsActions.resetError()),
})

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withStyles(styles)(EditArcAssignmentModal))
